import React from 'react';
import { useTranslation } from 'react-i18next';
import { RenderedHtml } from 'modules/editor';
import { ReviewOption } from './ReviewParts';
import type { AnswerReviewItem } from './AnswerReviewBody';

/**
 * The candidate's own answers with no right/wrong colouring and no correct-answer
 * rows, for results where the author withheld the answer key. Every field read
 * here is something the candidate submitted or the question structure that the
 * exam itself showed; nothing depends on the key being present.
 */

type Loose = Record<string, any>;

const isEmpty = (value: unknown): boolean => {
    if (value == null || value === '') return true;
    if (Array.isArray(value)) return value.every(isEmpty);
    if (typeof value === 'object') return Object.values(value as Loose).every(isEmpty);
    return String(value).trim() === '' || String(value) === '—';
};

const optionLabel = (options: Loose[] | undefined, id: unknown): string => {
    const option = (options ?? []).find((o) => String(o.id) === String(id));
    return String(option?.value ?? option?.text ?? id);
};

/** Plain text of one submitted value, resolving option ids and ordered item ids to their labels. */
const describeValue = (question: Loose, value: unknown): string => {
    if (isEmpty(value)) return '';
    const type = question.type ?? question.questionType;
    if (['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE'].includes(type)) {
        const ids = Array.isArray(value) ? value : [value];
        return ids.map((id) => optionLabel(question.options, id)).join(', ');
    }
    if (type === 'REORDER' && Array.isArray(value)) {
        const items: Loose[] = question.reorderItems ?? [];
        return value.map((id) => String(items.find((it) => it.id === id)?.content ?? id)).join(' → ');
    }
    if (Array.isArray(value)) return value.map(String).join(', ');
    if (typeof value === 'object') {
        const entries = Object.entries(value as Loose).filter(([, v]) => !isEmpty(v));
        return type === 'MATCHING'
            ? entries.map(([k, v]) => `${k} → ${v}`).join('; ')
            : entries.map(([, v]) => String(v)).join(', ');
    }
    return String(value);
};

const AnswerLine: React.FC<{ label?: React.ReactNode; text: string }> = ({ label, text }) => {
    const { t } = useTranslation();
    return (
        <div className="rounded-lg border border-border bg-card p-3 text-sm">
            {label != null && <div className="mb-1 font-semibold text-foreground">{label}</div>}
            <div className="break-words text-foreground">
                {text ? (
                    <RenderedHtml html={text} inline />
                ) : (
                    <span className="italic text-muted-foreground">— {t('not_answered')} —</span>
                )}
            </div>
        </div>
    );
};

const passageSubQuestions = (questionData: Loose): Loose[] => {
    if (Array.isArray(questionData.subQuestions) && questionData.subQuestions.length > 0) {
        return questionData.subQuestions;
    }
    return Array.isArray(questionData.pages)
        ? questionData.pages.flatMap((page: Loose) => (Array.isArray(page?.subQuestions) ? page.subQuestions : []))
        : [];
};

export const AnswerReviewHidden: React.FC<{ answer: AnswerReviewItem }> = ({ answer }) => {
    const { t } = useTranslation();
    const qd: Loose = answer.questionData ?? {};
    const raw = answer.answerRaw ?? answer.answer;

    if (answer.options) {
        return (
            <>
                {answer.options.map((opt) => (
                    <ReviewOption
                        key={opt.id}
                        html={opt.value || ''}
                        isCorrect={false}
                        isSelected={opt.isSelected}
                        multi={answer.questionType === 'MULTIPLE_RESPONSE'}
                        keyHidden
                    />
                ))}
            </>
        );
    }

    if (answer.matchPairs) {
        return (
            <div className="space-y-2">
                {answer.matchPairs.map((pair, idx) => (
                    <AnswerLine
                        key={pair.key || idx}
                        label={<RenderedHtml html={pair.key || ''} inline />}
                        text={(pair.selectedValue || '').trim()}
                    />
                ))}
            </div>
        );
    }

    if (answer.questionType === 'PASSAGE') {
        const given: Loose = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Loose) : {};
        return (
            <div className="space-y-2">
                {passageSubQuestions(qd).map((sub, idx) => (
                    <AnswerLine
                        key={sub.id ?? idx}
                        label={
                            <>
                                {t('question')} {idx + 1}
                                {(sub.questionText || sub.prompt) && (
                                    <span className="ml-2 font-normal">
                                        <RenderedHtml html={sub.questionText || sub.prompt} inline />
                                    </span>
                                )}
                            </>
                        }
                        text={describeValue(sub, given[sub.id])}
                    />
                ))}
            </div>
        );
    }

    if (answer.questionType === 'FILL_IN_BLANKS' && Array.isArray(qd.blanks) && raw && typeof raw === 'object') {
        const given = raw as Loose;
        return (
            <div className="space-y-2">
                {qd.blanks.map((blank: Loose, idx: number) => (
                    <AnswerLine
                        key={blank.id ?? idx}
                        label={`${t('reading_blank')} ${idx + 1}`}
                        text={describeValue({ type: 'SHORT_ANSWER' }, given[blank.id])}
                    />
                ))}
            </div>
        );
    }

    return <AnswerLine text={describeValue({ type: answer.questionType, reorderItems: qd.reorderItems }, raw)} />;
};

export default AnswerReviewHidden;
